import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { Button, Divider, Box, useMediaQuery } from '@mui/material';
import {
  OrderManagementPage,
  PageTitle,
  Table,
  SearchSection,
  Pagination,
  Loader
} from '../../../components';
import { Add, Edit, Delete } from '@mui/icons-material';
import { IChecklistTemplate, IChecklistTemplateDetail } from '../../../models';
import { ChecklistTemplateFilters } from './ChecklistTemplatesFilters';
import { ChecklistTemplateModal } from './ChecklistTemplateModal';
import { MobileChecklist } from './MobileChecklist';
import {
  getChecklistTemplates,
  getChecklistTemplate,
  deleteChecklistTemplate
} from '../../../fetch';
import { checklistsParentLink } from '../../../helpers';

export const ChecklistTemplatesList = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [selectedType, setSelectedType] = useState<string | number>('');
  const [checklistTemplates, setChecklistTemplates] = useState<IChecklistTemplate[]>([]);
  const [isLoadingTemplates, setLoadingTemplates] = useState<boolean>(false);
  const [isLoadingTemplate, setLoadingTemplate] = useState<boolean>(false);
  const [currentChecklistTemplate, setCurrentChecklistTemplate] =
    useState<IChecklistTemplateDetail | null>(null);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [isShowingChecklistTemplateModal, showChecklistTemplateModal] = useState(false);
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const [filters, setFilters] = useState<{
    typeId?: string | number;
  } | null>({});

  const isMobile = useMediaQuery('(max-width: 767px)');

  const fetchChecklistTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await getChecklistTemplates({
        page: page + 1,
        perPage,
        name: searchedValue,
        ...filters
      });
      setChecklistTemplates(res.records);
      setRecordCount(res.totalRecordCount);
    } catch (error) {
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    fetchChecklistTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, perPage, searchedValue]);

  const handleEdit = async (id: number) => {
    setLoadingTemplate(true);
    try {
      const res = await getChecklistTemplate(id);
      setCurrentChecklistTemplate(res);
      showChecklistTemplateModal(true);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleDelete = async id => {
    const result = window.confirm('Are you sure you want to delete this checklist template?');
    if (result) {
      setDeleting(true);
      try {
        await deleteChecklistTemplate(id);
        enqueueSnackbar(`Checklist template deleted!`, {
          variant: 'success'
        });
        fetchChecklistTemplates();
      } catch (error) {
        enqueueSnackbar('Error deleting checklist template, please refresh and try again.', {
          variant: 'error'
        });
        console.log(error);
      } finally {
        setDeleting(false);
      }
    }
  };

  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
      sort: false
    },
    {
      Header: 'Type',
      accessor: 'type',
      sort: false
    },
    {
      Header: 'Task ID',
      accessor: 'sageTaskId',
      sort: false
    },
    {
      id: 'actions',
      accessor: '',
      Header: '',
      sort: false,
      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: IChecklistTemplate } };
      }) => {
        return (
          <Box textAlign="right">
            <Button
              id="edit"
              color="secondary"
              startIcon={<Edit />}
              onClick={() => {
                handleEdit(original.checklistTemplateId);
              }}
            >
              Edit
            </Button>
            <Button
              sx={theme => ({
                marginLeft: theme.spacing(0.5),
                color: theme.palette.error.main
              })}
              startIcon={<Delete />}
              onClick={() => handleDelete(original.checklistTemplateId)}
            >
              Delete
            </Button>
          </Box>
        );
      }
    }
  ];
  return (
    <>
      {(isLoadingTemplate || isDeleting) && (
        <Loader type="fullscreen" position="centered">
          Loading...
        </Loader>
      )}
      <OrderManagementPage
        title="Checklist Templates"
        requiredFeature={checklistsParentLink.featureFlag}
        legacyUrl={checklistsParentLink.legacyUrl}
      >
        <Box display="flex" flexWrap="wrap">
          <Box
            sx={{
              '@media (min-width: 768px)': {
                flex: 1
              }
            }}
          >
            <PageTitle showDivider={false} title="Checklist Templates" />
          </Box>
          <Box
            sx={{
              width: '100%',
              marginBottom: '1rem',
              '@media (min-width: 768px)': {
                textAlign: 'right',
                width: 'auto',
                marginBottom: 0
              }
            }}
          >
            <Button
              id="add-checklist"
              fullWidth
              onClick={() => {
                showChecklistTemplateModal(true);
              }}
              startIcon={<Add />}
              color="primary"
              variant="contained"
            >
              Add Checklist
            </Button>
          </Box>
        </Box>
        <Divider />
        <ChecklistTemplateFilters
          isLoading={false}
          handleSearch={(val: string) => {
            setPage(0);
            setSearchedValue(val);
          }}
          applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
            if (clearFilters) {
              setFilters({
                typeId: ''
              });
            } else {
              setPage(0);
              setFilters({
                ...filters,
                typeId: selectedType || undefined
              });
            }
          }}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          setSearchValue={setSearchValue}
          setSearchedValue={setSearchedValue}
          searchValue={searchValue}
          hasAppliedFilters={hasAppliedFilters}
          setHasAppliedFilters={setHasAppliedFilters}
        />
        {searchedValue && (
          <SearchSection
            searchedValue={searchedValue}
            handleClearSearch={() => {
              setSearchValue('');
              setSearchedValue('');
              setHasAppliedFilters(false);
              setPage(0);
            }}
          />
        )}
        <Table
          columns={columns}
          data={checklistTemplates}
          isLoading={isLoadingTemplates}
          hidePagination
          ResponsiveComponent={isMobile ? MobileChecklist : undefined}
          mobileProps={{
            handleEdit: (val: IChecklistTemplate) => handleEdit(val.checklistTemplateId),
            handleDelete: (val: IChecklistTemplate) => handleDelete(val.checklistTemplateId)
          }}
        />
        {!isLoadingTemplates && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
      </OrderManagementPage>
      <ChecklistTemplateModal
        currentChecklistTemplate={currentChecklistTemplate}
        fetchChecklistTemplates={fetchChecklistTemplates}
        open={isShowingChecklistTemplateModal}
        onClose={() => {
          setCurrentChecklistTemplate(null);
          showChecklistTemplateModal(false);
        }}
      />
    </>
  );
};
